import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Package, Plus, Truck, ChevronRight, X, Search,
  CheckCircle, XCircle, RefreshCw, MessageSquare, Mail, Send,
  Building2, Calendar, MapPin, Trash2, Phone, FileText,
  TrendingDown, Star, AlertTriangle, Tag, Info
} from 'lucide-react';
import api from '../../utils/api';
import {
  getVendors, createVendor, getIndents, getIndentById,
  createIndent, deleteIndent, updateStatus, notifySupplier, acknowledgeIndent,
  removeItem, searchMaterials, getPlanRequirements,
  getVariantsByMaterial, getSupplierRatesForVariant, getBestSupplierForMaterial,
  getSupplierRatesForVendor, upsertSupplierRate, deleteSupplierRate,
  getSupplierContactConfig, upsertSupplierContactConfig,
  Vendor, Indent, PlanRequirement, MaterialResult,
} from '../../services/materialIndent.service';

// ── Types ─────────────────────────────────────────────────────

interface Project { project_id: number; project_code: string; project_name: string; }
type PageTab = 'indents' | 'vendors' | 'rates';

interface Variant {
  variant_id: number; variant_code: string; brand_name: string;
  variant_name: string; quality_tier: string; specification: string;
  gst_rate_pct: number; unit_of_measure: string;
}

interface SupplierRate {
  rate_id: number; sup_id: number; variant_id: number;
  price_per_unit: number; unit_of_measure: string;
  includes_delivery: boolean; delivery_charges: number;
  min_order_qty: number; bulk_discount_pct: number; effective_price: number;
  supplier_name: string; supplier_mobile: string; supplier_city: string;
  remarks: string | null;
}

interface BestSupplier {
  sup_id: number; variant_id: number; price_per_unit: number;
  effective_price: number; supplier_name: string;
  brand_name: string; variant_name: string; quality_tier: string;
}

interface BasketItem {
  mat_id: number; mat_code: string; mat_name: string;
  variant_id: number | null; brand_name: string; variant_name: string;
  sup_id: number | null; supplier_name: string;
  required_qty: number; unit: string; rate_per_unit: number; notes: string;
  plan_material_req_id: number | null;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  DRAFT:               { label: 'Draft',            color: 'text-gray-600',   bg: 'bg-gray-100'   },
  SENT:                { label: 'Sent',              color: 'text-blue-700',   bg: 'bg-blue-100'   },
  ACKNOWLEDGED:        { label: 'Acknowledged',      color: 'text-indigo-700', bg: 'bg-indigo-100' },
  PARTIALLY_DELIVERED: { label: 'Partly Delivered',  color: 'text-amber-700',  bg: 'bg-amber-100'  },
  DELIVERED:           { label: 'Delivered',         color: 'text-green-700',  bg: 'bg-green-100'  },
  CANCELLED:           { label: 'Cancelled',         color: 'text-red-600',    bg: 'bg-red-100'    },
};

const QUALITY_COLORS: Record<string, string> = {
  ECONOMY: 'bg-gray-100 text-gray-600',
  STANDARD: 'bg-blue-100 text-blue-700',
  PREMIUM: 'bg-amber-100 text-amber-700',
};

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (n: number | null) =>
  n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

// ── Material + Variant + Supplier Picker ──────────────────────

interface PickerProps { onAdd: (item: BasketItem) => void; }

const MaterialPicker: React.FC<PickerProps> = ({ onAdd }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MaterialResult[]>([]);
  const [selected, setSelected] = useState<MaterialResult | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [variantId, setVariantId] = useState<number | null>(null);
  const [supplierRates, setSupplierRates] = useState<SupplierRate[]>([]);
  const [bestSuppliers, setBestSuppliers] = useState<BestSupplier[]>([]);
  const [pickedSupplier, setPickedSupplier] = useState<SupplierRate | null>(null);
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingVariants, setLoadingVariants] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = (val: string) => {
    setQ(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try { setResults(await searchMaterials(val)); } catch { setResults([]); }
    }, 300);
  };

  const pickMaterial = async (mat: MaterialResult) => {
    setSelected(mat); setResults([]); setQ(mat.mat_name);
    setUnit(mat.unit_of_measure); setVariantId(null); setPickedSupplier(null);
    setSupplierRates([]); setRate('');
    setLoadingVariants(true);
    try {
      const [vars, best] = await Promise.all([
        getVariantsByMaterial(mat.mat_id),
        getBestSupplierForMaterial(mat.mat_id),
      ]);
      setVariants(vars);
      setBestSuppliers(best);
      // Auto-select first (cheapest) option
      if (best.length > 0) {
        setVariantId(best[0].variant_id);
        setRate(String(best[0].effective_price));
        loadRatesForVariant(best[0].variant_id);
      }
    } catch { } finally { setLoadingVariants(false); }
  };

  const loadRatesForVariant = async (vid: number) => {
    try {
      const rates = await getSupplierRatesForVariant(vid);
      setSupplierRates(rates);
      if (rates.length > 0) {
        setPickedSupplier(rates[0]); // cheapest
        setRate(String(rates[0].effective_price));
      }
    } catch { setSupplierRates([]); }
  };

  const onVariantChange = (vid: number) => {
    setVariantId(vid); setPickedSupplier(null); setRate('');
    loadRatesForVariant(vid);
  };

  const onSupplierPick = (sr: SupplierRate) => {
    setPickedSupplier(sr);
    setRate(String(sr.effective_price));
  };

  const handleAdd = () => {
    if (!selected || !qty || !rate) return;
    const chosenVariant = variants.find(v => v.variant_id === variantId);
    onAdd({
      mat_id: selected.mat_id, mat_code: selected.mat_code, mat_name: selected.mat_name,
      variant_id: variantId, brand_name: chosenVariant?.brand_name || '',
      variant_name: chosenVariant?.variant_name || '',
      sup_id: pickedSupplier?.sup_id || null,
      supplier_name: pickedSupplier?.supplier_name || '',
      required_qty: parseFloat(qty), unit: unit || selected.unit_of_measure,
      rate_per_unit: parseFloat(rate), notes, plan_material_req_id: null,
    });
    setQ(''); setSelected(null); setVariants([]); setVariantId(null);
    setSupplierRates([]); setPickedSupplier(null); setBestSuppliers([]);
    setQty(''); setRate(''); setNotes('');
  };

  return (
    <div className="border border-dashed border-gray-300 rounded-xl p-4 space-y-3 bg-gray-50">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Material</p>

      {/* Material search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-2.5 text-gray-400"/>
        <input value={q} onChange={e => search(e.target.value)} placeholder="Search material by name or code…"
          className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"/>
        {results.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
            {results.map(m => (
              <button key={m.mat_id} onClick={() => pickMaterial(m)}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0">
                <p className="text-sm font-medium text-gray-800">{m.mat_name}</p>
                <p className="text-xs text-gray-400">{m.mat_code} · {m.category} · {m.unit_of_measure}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {loadingVariants && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <RefreshCw size={13} className="animate-spin"/> Loading variants & prices…
        </div>
      )}

      {selected && !loadingVariants && (
        <>
          {/* Best price banner */}
          {bestSuppliers.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700 mb-2">
                <TrendingDown size={13}/> Best Price Options
              </div>
              <div className="space-y-1.5">
                {bestSuppliers.slice(0, 3).map((bs, i) => (
                  <button key={bs.sup_id + '-' + bs.variant_id} onClick={() => { setVariantId(bs.variant_id); setRate(String(bs.effective_price)); loadRatesForVariant(bs.variant_id); }}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${variantId === bs.variant_id && rate === String(bs.effective_price) ? 'border-green-500 bg-white' : 'border-transparent bg-white hover:border-green-300'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium mr-2 ${QUALITY_COLORS[bs.quality_tier] || 'bg-gray-100 text-gray-600'}`}>{bs.quality_tier}</span>
                        <span className="text-xs font-medium text-gray-700">{bs.brand_name} — {bs.variant_name}</span>
                        <span className="text-xs text-gray-400 ml-2">via {bs.supplier_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {i === 0 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Lowest</span>}
                        <span className="text-sm font-bold text-gray-900">{fmt(bs.effective_price)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Variant dropdown + supplier rates */}
          {variants.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 font-medium">Brand / Variant</label>
                <select value={variantId || ''} onChange={e => onVariantChange(parseInt(e.target.value))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none">
                  <option value="">Select variant…</option>
                  {variants.map(v => (
                    <option key={v.variant_id} value={v.variant_id}>
                      {v.brand_name} — {v.variant_name} ({v.quality_tier})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Supplier (by price)</label>
                <select value={pickedSupplier?.sup_id || ''} onChange={e => { const sr = supplierRates.find(r => r.sup_id === parseInt(e.target.value)); if (sr) onSupplierPick(sr); }}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none">
                  <option value="">Select supplier…</option>
                  {supplierRates.map(sr => (
                    <option key={sr.sup_id} value={sr.sup_id}>
                      {sr.supplier_name} — {fmt(sr.effective_price)}/{unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* No variants — manual entry */}
          {variants.length === 0 && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <Info size={13}/> No variants registered. Enter rate manually below.
            </div>
          )}

          {/* Qty / Unit / Rate */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500">Quantity *</label>
              <input type="number" value={qty} onChange={e => setQty(e.target.value)} min="0" step="0.01"
                className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
            </div>
            <div>
              <label className="text-xs text-gray-500">Unit</label>
              <input value={unit} onChange={e => setUnit(e.target.value)}
                className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
            </div>
            <div>
              <label className="text-xs text-gray-500">Rate/Unit (₹) *</label>
              <input type="number" value={rate} onChange={e => setRate(e.target.value)} min="0" step="0.01"
                className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Notes / Spec</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
            </div>
            <div className="flex items-end">
              {qty && rate && (
                <span className="text-xs text-gray-500 mb-2 mr-2">= {fmt(parseFloat(qty) * parseFloat(rate))}</span>
              )}
              <button onClick={handleAdd} disabled={!qty || !rate}
                className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-40 hover:bg-blue-700 flex items-center justify-center gap-1">
                <Plus size={14}/> Add
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Create Indent Modal ────────────────────────────────────────

const CreateIndentModal: React.FC<{
  projectId: number; vendors: Vendor[];
  onClose: () => void; onCreated: () => void; onNeedVendor: () => void;
}> = ({ projectId, vendors, onClose, onCreated, onNeedVendor }) => {
  const [supplierId, setSupplierId] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [planReqs, setPlanReqs] = useState<PlanRequirement[]>([]);
  const [showPlanReqs, setShowPlanReqs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { getPlanRequirements(projectId).then(setPlanReqs).catch(() => {}); }, [projectId]);

  const addFromPlan = (req: PlanRequirement) => {
    if (basket.some(i => i.mat_id === req.mat_id)) return;
    setBasket(prev => [...prev, {
      mat_id: req.mat_id, mat_code: req.mat_code, mat_name: req.mat_name,
      variant_id: null, brand_name: '', variant_name: '',
      sup_id: null, supplier_name: '',
      required_qty: Number(req.qty_with_wastage || req.required_qty),
      unit: req.unit, rate_per_unit: Number(req.supplier_rate || 0),
      notes: req.notes || '', plan_material_req_id: req.req_id,
    }]);
  };

  const removeRow = (idx: number) => setBasket(prev => prev.filter((_, i) => i !== idx));
  const totalValue = basket.reduce((s, i) => s + i.required_qty * i.rate_per_unit, 0);

  // Auto-detect supplier from basket items (most common sup_id)
  useEffect(() => {
    const supIds = basket.map(i => i.sup_id).filter(Boolean) as number[];
    if (supIds.length > 0 && !supplierId) {
      const mode = supIds.sort((a, b) => supIds.filter(v => v === a).length - supIds.filter(v => v === b).length).pop()!;
      setSupplierId(String(mode));
    }
  }, [basket]);

  const handleSave = async () => {
    if (!supplierId) { setError('Please select a supplier'); return; }
    if (!requiredDate) { setError('Required date is mandatory'); return; }
    if (basket.length === 0) { setError('Add at least one item'); return; }
    setSaving(true); setError('');
    try {
      await createIndent({
        supplier_id: parseInt(supplierId), project_id: projectId,
        required_date: requiredDate, delivery_address: deliveryAddress, notes,
        items: basket.map(i => ({
          mat_id: i.mat_id, variant_id: i.variant_id || undefined,
          required_qty: i.required_qty, unit: i.unit,
          rate_per_unit: i.rate_per_unit, notes: i.notes,
          plan_material_req_id: i.plan_material_req_id || undefined,
        })),
      });
      onCreated();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to create indent'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
      <div className="w-full max-w-2xl bg-white min-h-screen shadow-2xl flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New Material Indent</h2>
            <p className="text-xs text-gray-400 mt-0.5">Prices auto-suggested from supplier rate list</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
        </div>

        <div className="flex-1 px-6 py-4 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm text-red-700">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
              {vendors.length === 0 ? (
                <div className="border border-dashed border-orange-300 bg-orange-50 rounded-lg p-3 text-sm text-orange-700 flex items-center justify-between">
                  No suppliers. <button onClick={onNeedVendor} className="text-blue-600 underline text-xs ml-2">Add Vendor →</button>
                </div>
              ) : (
                <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none">
                  <option value="">Select supplier…</option>
                  {vendors.map(v => <option key={v.vnd_id} value={v.vnd_id}>{v.company_name}{v.city ? ` — ${v.city}` : ''}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Required By *</label>
              <input type="date" value={requiredDate} onChange={e => setRequiredDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"/>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
              <input value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} placeholder="Site address…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"/>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"/>
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Items</p>
              {planReqs.length > 0 && (
                <button onClick={() => setShowPlanReqs(s => !s)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <Package size={12}/> From Plan ({planReqs.length})
                </button>
              )}
            </div>
            {showPlanReqs && (
              <div className="mb-3 border border-blue-100 rounded-lg bg-blue-50 max-h-40 overflow-y-auto">
                {planReqs.map(r => (
                  <div key={r.req_id} className="flex items-center justify-between px-3 py-2 border-b border-blue-100 last:border-0">
                    <div>
                      <p className="text-xs font-medium text-gray-800">{r.mat_name}</p>
                      <p className="text-xs text-gray-500">{fmtQty(r.qty_with_wastage || r.required_qty)} {r.unit}</p>
                    </div>
                    <button onClick={() => addFromPlan(r)} disabled={basket.some(i => i.mat_id === r.mat_id)}
                      className="text-xs text-blue-600 border border-blue-300 px-2 py-0.5 rounded hover:bg-blue-100 disabled:opacity-40">
                      {basket.some(i => i.mat_id === r.mat_id) ? 'Added' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {basket.length > 0 && (
              <div className="mb-3 border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-500">
                    <th className="text-left px-3 py-2">Material / Brand</th>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Rate</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="px-2"/>
                  </tr></thead>
                  <tbody>
                    {basket.map((item, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <p className="font-medium text-gray-800">{item.mat_name}</p>
                          {item.brand_name && <p className="text-gray-400">{item.brand_name} · {item.variant_name}</p>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{item.supplier_name || '—'}</td>
                        <td className="px-3 py-2 text-right">{fmtQty(item.required_qty)} {item.unit}</td>
                        <td className="px-3 py-2 text-right">{fmt(item.rate_per_unit)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt(item.required_qty * item.rate_per_unit)}</td>
                        <td className="px-2 py-2"><button onClick={() => removeRow(idx)}><X size={14} className="text-red-400 hover:text-red-600"/></button></td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
                      <td className="px-3 py-2 text-gray-700" colSpan={4}>Total ({basket.length} items)</td>
                      <td className="px-3 py-2 text-right text-gray-900">{fmt(totalValue)}</td>
                      <td/>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <MaterialPicker onAdd={item => setBasket(prev => [...prev, item])}/>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between sticky bottom-0">
          <span className="text-sm text-gray-500">{basket.length} items · {fmt(totalValue)}</span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
            <button onClick={handleSave} disabled={saving || vendors.length === 0}
              className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin"/>} Create Draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Add Vendor Modal ──────────────────────────────────────────

const AddVendorModal: React.FC<{ onClose: () => void; onCreated: (v: Vendor) => void }> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ company_name: '', contact_person: '', mobile: '', email: '', city: '', state: '', address: '', gstin: '', payment_terms: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(p => ({ ...p, [k]: e.target.value }));
  const handleSave = async () => {
    if (!form.company_name.trim()) { setError('Company name required'); return; }
    setSaving(true); setError('');
    try { const v = await createVendor(form); onCreated(v); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold">Add New Vendor</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500">Company Name *</label>
              <input value={form.company_name} onChange={set('company_name')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">Contact Person</label>
              <input value={form.contact_person} onChange={set('contact_person')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">Mobile</label>
              <input value={form.mobile} onChange={set('mobile')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">Email</label>
              <input value={form.email} onChange={set('email')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">GSTIN</label>
              <input value={form.gstin} onChange={set('gstin')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">City</label>
              <input value={form.city} onChange={set('city')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
            <div><label className="text-xs text-gray-500">Payment Terms</label>
              <select value={form.payment_terms} onChange={set('payment_terms')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">Select…</option>
                {['Immediate','Net 15','Net 30','Net 45','Net 60','On Delivery'].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div className="col-span-2"><label className="text-xs text-gray-500">Address</label>
              <input value={form.address} onChange={set('address')} className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm"/></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
            {saving && <RefreshCw size={14} className="animate-spin"/>} Add Vendor
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Notify Modal ──────────────────────────────────────────────

const NotifyModal: React.FC<{ indent: Indent; onClose: () => void; onDone: () => void }> = ({ indent, onClose, onDone }) => {
  const [via, setVia] = useState<'EMAIL' | 'WHATSAPP' | 'BOTH'>('WHATSAPP');
  const [saving, setSaving] = useState(false);
  const handleSend = async () => {
    setSaving(true);
    try { await notifySupplier(indent.indent_id, via); onDone(); }
    catch { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Notify Supplier</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <p className="text-sm text-gray-600">Send <strong>{indent.indent_code}</strong> to <strong>{indent.supplier_name}</strong></p>
        <div className="flex gap-2">
          {(['EMAIL', 'WHATSAPP', 'BOTH'] as const).map(v => (
            <button key={v} onClick={() => setVia(v)} className={`flex-1 py-2.5 text-sm rounded-lg border-2 font-medium transition-colors ${via === v ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              {v === 'EMAIL' && <Mail size={14} className="inline mr-1"/>}
              {v === 'WHATSAPP' && <MessageSquare size={14} className="inline mr-1"/>}
              {v === 'BOTH' && <Send size={14} className="inline mr-1"/>}
              {v}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={handleSend} disabled={saving} className="flex-1 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin"/> : <Send size={14}/>} Mark Sent
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Indent Detail Drawer ──────────────────────────────────────

const IndentDetail: React.FC<{ indentId: number; onClose: () => void; onRefresh: () => void }> = ({ indentId, onClose, onRefresh }) => {
  const [indent, setIndent] = useState<Indent | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNotify, setShowNotify] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setIndent(await getIndentById(indentId)); } catch {} finally { setLoading(false); }
  }, [indentId]);

  useEffect(() => { load(); }, [load]);

  const action = async (fn: () => Promise<any>) => {
    setActionLoading(true);
    try { await fn(); await load(); onRefresh(); }
    catch (e: any) { alert(e?.response?.data?.message || 'Action failed'); }
    finally { setActionLoading(false); }
  };

  if (loading) return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-2xl bg-white shadow-2xl flex items-center justify-center">
      <RefreshCw size={24} className="animate-spin text-blue-600"/>
    </div>
  );
  if (!indent) return null;

  const sm = STATUS_META[indent.status] || STATUS_META.DRAFT;
  const canNotify = ['DRAFT', 'SENT'].includes(indent.status);
  const canAck = indent.status === 'SENT';
  const canPartial = indent.status === 'ACKNOWLEDGED';
  const canDeliver = ['ACKNOWLEDGED', 'PARTIALLY_DELIVERED'].includes(indent.status);
  const canCancel = !['DELIVERED', 'CANCELLED'].includes(indent.status);
  const canDelete = indent.status === 'DRAFT';

  return (
    <>
      {showNotify && <NotifyModal indent={indent} onClose={() => setShowNotify(false)} onDone={() => { setShowNotify(false); load(); onRefresh(); }}/>}
      <div className="fixed inset-0 z-40 flex">
        <div className="flex-1 bg-black/30" onClick={onClose}/>
        <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-start justify-between px-6 py-4 border-b">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold">{indent.indent_code}</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${sm.bg} ${sm.color}`}>{sm.label}</span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{indent.project_code} · {indent.project_name}</p>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-2 text-sm">
              <div className="col-span-2 font-semibold text-gray-800 flex items-center gap-2"><Building2 size={15} className="text-blue-600"/>{indent.supplier_name}</div>
              {indent.supplier_mobile && <div className="text-gray-500 flex items-center gap-1"><Phone size={12}/>{indent.supplier_mobile}</div>}
              {indent.supplier_email && <div className="text-gray-500 text-xs">{indent.supplier_email}</div>}
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><Calendar size={11}/>Indent Date</p><p className="font-medium">{fmtDate(indent.indent_date)}</p></div>
              <div className="border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><Calendar size={11}/>Required By</p><p className="font-medium">{fmtDate(indent.required_date)}</p></div>
              <div className="border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-400 mb-1">Total Value</p><p className="font-bold">{fmt(indent.total_value)}</p></div>
            </div>
            {indent.supplier_notified_at && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <CheckCircle size={15}/> Notified via <strong>{indent.notified_via}</strong> on {fmtDate(indent.supplier_notified_at)}
              </div>
            )}
            {indent.delivery_address && (
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2.5">
                <MapPin size={15} className="mt-0.5 text-gray-400 flex-shrink-0"/><span>{indent.delivery_address}</span>
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Items ({indent.items?.length || 0})</p>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-500">
                    <th className="text-left px-4 py-2.5">Material</th>
                    <th className="text-right px-4 py-2.5">Required</th>
                    <th className="text-right px-4 py-2.5">Rate</th>
                    <th className="text-right px-4 py-2.5">Amount</th>
                  </tr></thead>
                  <tbody>
                    {(indent.items || []).map(item => (
                      <tr key={item.item_id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-800">{item.mat_name}</p>
                          <p className="text-gray-400">{item.mat_code} · {item.category}</p>
                        </td>
                        <td className="px-4 py-2.5 text-right">{fmtQty(item.required_qty)} {item.unit}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmt(item.rate_per_unit)}</td>
                        <td className="px-4 py-2.5 text-right font-medium">{fmt(item.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
                    <td className="px-4 py-2.5 text-gray-700" colSpan={3}>Total</td>
                    <td className="px-4 py-2.5 text-right text-gray-900">{fmt(indent.total_value)}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex flex-wrap gap-2">
              {canNotify && <button onClick={() => setShowNotify(true)} disabled={actionLoading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5"><Send size={14}/>{indent.status === 'DRAFT' ? 'Send to Supplier' : 'Resend'}</button>}
              {canAck && <button onClick={() => action(() => acknowledgeIndent(indent.indent_id))} disabled={actionLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5"><CheckCircle size={14}/>Mark Acknowledged</button>}
              {canPartial && <button onClick={() => action(() => updateStatus(indent.indent_id, 'PARTIALLY_DELIVERED'))} disabled={actionLoading} className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-1.5"><Truck size={14}/>Partial Delivery</button>}
              {canDeliver && <button onClick={() => action(() => updateStatus(indent.indent_id, 'DELIVERED'))} disabled={actionLoading} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5"><CheckCircle size={14}/>Mark Delivered</button>}
              {canCancel && <button onClick={() => { if (window.confirm('Cancel this indent?')) action(() => updateStatus(indent.indent_id, 'CANCELLED')); }} disabled={actionLoading} className="px-4 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1.5"><XCircle size={14}/>Cancel</button>}
              {canDelete && <button onClick={() => { if (window.confirm('Delete draft?')) action(async () => { await deleteIndent(indent.indent_id); onClose(); onRefresh(); }); }} disabled={actionLoading} className="ml-auto px-4 py-2 text-sm border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-100 flex items-center gap-1.5"><Trash2 size={14}/>Delete Draft</button>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Supplier Rates Tab ────────────────────────────────────────

const SupplierRatesTab: React.FC<{ vendors: Vendor[] }> = ({ vendors }) => {
  const [selectedVendor, setSelectedVendor] = useState<number | null>(null);
  const [rates, setRates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);
  // Add rate form state
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState<MaterialResult[]>([]);
  const [selectedMat, setSelectedMat] = useState<MaterialResult | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [rateForm, setRateForm] = useState({ variant_id: '', price_per_unit: '', unit_of_measure: '', includes_delivery: false, delivery_charges: '0', min_order_qty: '0', bulk_discount_pct: '0', remarks: '' });
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadRates = async (vid: number) => {
    setLoading(true);
    try { setRates(await getSupplierRatesForVendor(vid)); }
    catch { setRates([]); }
    finally { setLoading(false); }
  };

  const onVendorChange = (vid: number) => {
    setSelectedVendor(vid);
    loadRates(vid);
  };

  const searchMat = (val: string) => {
    setMatSearch(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.length < 2) { setMatResults([]); return; }
    timer.current = setTimeout(async () => {
      try { setMatResults(await searchMaterials(val)); } catch { setMatResults([]); }
    }, 300);
  };

  const pickMat = async (mat: MaterialResult) => {
    setSelectedMat(mat); setMatSearch(mat.mat_name); setMatResults([]);
    setRateForm(f => ({ ...f, unit_of_measure: mat.unit_of_measure, variant_id: '' }));
    try { const vars = await getVariantsByMaterial(mat.mat_id); setVariants(vars); }
    catch { setVariants([]); }
  };

  const handleAddRate = async () => {
    if (!selectedVendor || !rateForm.variant_id || !rateForm.price_per_unit) return;
    setSaving(true);
    try {
      await upsertSupplierRate({ sup_id: selectedVendor, ...rateForm, variant_id: parseInt(rateForm.variant_id), price_per_unit: parseFloat(rateForm.price_per_unit), delivery_charges: parseFloat(rateForm.delivery_charges || '0'), min_order_qty: parseFloat(rateForm.min_order_qty || '0'), bulk_discount_pct: parseFloat(rateForm.bulk_discount_pct || '0') });
      setShowAddRate(false); setSelectedMat(null); setMatSearch(''); setVariants([]);
      setRateForm({ variant_id: '', price_per_unit: '', unit_of_measure: '', includes_delivery: false, delivery_charges: '0', min_order_qty: '0', bulk_discount_pct: '0', remarks: '' });
      loadRates(selectedVendor);
    } catch (e: any) { alert(e?.response?.data?.message || 'Failed to save rate'); }
    finally { setSaving(false); }
  };

  const handleDeleteRate = async (rateId: number) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await deleteSupplierRate(rateId); if (selectedVendor) loadRates(selectedVendor); }
    catch { alert('Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-xs">
          <label className="text-xs text-gray-500 mb-1 block">Select Vendor</label>
          <select value={selectedVendor || ''} onChange={e => onVendorChange(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none">
            <option value="">Choose vendor…</option>
            {vendors.map(v => <option key={v.vnd_id} value={v.vnd_id}>{v.company_name}</option>)}
          </select>
        </div>
        {selectedVendor && (
          <button onClick={() => setShowAddRate(s => !s)} className="mt-5 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Rate
          </button>
        )}
      </div>

      {showAddRate && selectedVendor && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-800">Add Supplier Rate</p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400"/>
            <input value={matSearch} onChange={e => searchMat(e.target.value)} placeholder="Search material…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
            {matResults.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-40 overflow-y-auto">
                {matResults.map(m => (
                  <button key={m.mat_id} onClick={() => pickMat(m)} className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-0 text-sm">
                    {m.mat_name} <span className="text-gray-400 text-xs ml-1">{m.mat_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedMat && variants.length === 0 && <p className="text-xs text-amber-600">No variants for this material. Add variants first in the Materials master.</p>}
          {selectedMat && variants.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Brand / Variant *</label>
                <select value={rateForm.variant_id} onChange={e => setRateForm(f => ({ ...f, variant_id: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                  <option value="">Select variant…</option>
                  {variants.map(v => <option key={v.variant_id} value={v.variant_id}>{v.brand_name} — {v.variant_name} ({v.quality_tier})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Price per Unit (₹) *</label>
                <input type="number" value={rateForm.price_per_unit} onChange={e => setRateForm(f => ({ ...f, price_per_unit: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
              </div>
              <div>
                <label className="text-xs text-gray-500">Unit</label>
                <input value={rateForm.unit_of_measure} onChange={e => setRateForm(f => ({ ...f, unit_of_measure: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <input type="checkbox" checked={rateForm.includes_delivery} onChange={e => setRateForm(f => ({ ...f, includes_delivery: e.target.checked }))} id="incDel" className="rounded"/>
                <label htmlFor="incDel" className="text-xs text-gray-600">Price includes delivery</label>
                {!rateForm.includes_delivery && (
                  <div className="flex items-center gap-2 ml-4">
                    <label className="text-xs text-gray-500">Delivery charge (₹)</label>
                    <input type="number" value={rateForm.delivery_charges} onChange={e => setRateForm(f => ({ ...f, delivery_charges: e.target.value }))}
                      className="w-24 px-2 py-1 border border-gray-200 rounded text-sm bg-white"/>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Min Order Qty</label>
                <input type="number" value={rateForm.min_order_qty} onChange={e => setRateForm(f => ({ ...f, min_order_qty: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
              </div>
              <div>
                <label className="text-xs text-gray-500">Bulk Discount %</label>
                <input type="number" value={rateForm.bulk_discount_pct} onChange={e => setRateForm(f => ({ ...f, bulk_discount_pct: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Remarks</label>
                <input value={rateForm.remarks} onChange={e => setRateForm(f => ({ ...f, remarks: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"/>
              </div>
              <div className="col-span-2 flex justify-end gap-3">
                <button onClick={() => setShowAddRate(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
                <button onClick={handleAddRate} disabled={saving || !rateForm.variant_id || !rateForm.price_per_unit}
                  className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
                  {saving && <RefreshCw size={14} className="animate-spin"/>} Save Rate
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rates table */}
      {selectedVendor && (
        loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"/>)}</div>
        ) : rates.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Tag size={32} className="mx-auto mb-2 opacity-30"/>
            <p className="text-sm">No rates set for this vendor yet.</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 text-xs text-gray-500">
                <th className="text-left px-4 py-3">Material</th>
                <th className="text-left px-4 py-3">Brand / Variant</th>
                <th className="text-left px-4 py-3">Quality</th>
                <th className="text-right px-4 py-3">Price/Unit</th>
                <th className="text-right px-4 py-3">Effective</th>
                <th className="text-right px-4 py-3">Min Qty</th>
                <th className="px-3 py-3"/>
              </tr></thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.rate_id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3"><p className="font-medium text-gray-800">{r.mat_name}</p><p className="text-xs text-gray-400">{r.mat_code} · {r.category}</p></td>
                    <td className="px-4 py-3 text-gray-700">{r.brand_name} — {r.variant_name}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded font-medium ${QUALITY_COLORS[r.quality_tier] || 'bg-gray-100 text-gray-600'}`}>{r.quality_tier}</span></td>
                    <td className="px-4 py-3 text-right">{fmt(r.price_per_unit)}/{r.unit_of_measure}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(parseFloat(r.price_per_unit) + parseFloat(r.delivery_charges || 0))}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{r.min_order_qty || '—'}</td>
                    <td className="px-3 py-3"><button onClick={() => handleDeleteRate(r.rate_id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────

const MaterialIndentPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [tab, setTab] = useState<PageTab>('indents');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [indents, setIndents] = useState<Indent[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [selectedIndentId, setSelectedIndentId] = useState<number | null>(null);

  useEffect(() => {
    api.get('/projects').then((r: any) => {
      const list: Project[] = r.data.data || [];
      setProjects(list);
      if (list.length === 1) setProjectId(list[0].project_id);
    }).catch(() => {});
    getVendors().then(setVendors).catch(() => {});
  }, []);

  const loadIndents = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try { setIndents(await getIndents(projectId)); } catch {} finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadIndents(); }, [loadIndents]);

  const filtered = statusFilter === 'ALL' ? indents : indents.filter(i => i.status === statusFilter);
  const StatusBadge = ({ s }: { s: string }) => {
    const m = STATUS_META[s] || STATUS_META.DRAFT;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.bg} ${m.color}`}>{m.label}</span>;
  };

  const TAB_LABELS: Record<PageTab, string> = {
    indents: `Indents (${indents.length})`,
    vendors: `Vendors (${vendors.length})`,
    rates: 'Supplier Rates',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center"><Package size={18} className="text-white"/></div>
            <div><h1 className="text-lg font-bold text-gray-900">Material Indents</h1><p className="text-xs text-gray-400">Purchase indent management with supplier price comparison</p></div>
          </div>
          <div className="flex items-center gap-3">
            <select value={projectId ?? ''} onChange={e => setProjectId(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none min-w-52">
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} — {p.project_name}</option>)}
            </select>
            {projectId && tab === 'indents' && (
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                <Plus size={16}/> New Indent
              </button>
            )}
            {tab === 'vendors' && (
              <button onClick={() => setShowAddVendor(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                <Plus size={16}/> Add Vendor
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-1 mt-4">
          {(Object.keys(TAB_LABELS) as PageTab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${tab === t ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 p-6">
        {/* INDENTS */}
        {tab === 'indents' && (
          !projectId ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400"><Package size={48} className="mb-3 opacity-30"/><p className="text-lg font-medium">Select a project to view indents</p></div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => setStatusFilter('ALL')} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${statusFilter === 'ALL' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>All ({indents.length})</button>
                {Object.entries(STATUS_META).map(([key, meta]) => {
                  const count = indents.filter(i => i.status === key).length;
                  if (count === 0 && statusFilter !== key) return null;
                  return (
                    <button key={key} onClick={() => setStatusFilter(key)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${statusFilter === key ? `${meta.bg} ${meta.color} border-current` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                      {meta.label} ({count})
                    </button>
                  );
                })}
              </div>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-200 animate-pulse"/>)}</div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <FileText size={40} className="mb-3 opacity-30"/>
                  <p className="font-medium">{indents.length === 0 ? 'No indents yet' : 'No indents with this status'}</p>
                  {indents.length === 0 && <button onClick={() => setShowCreate(true)} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"><Plus size={15}/>Create First Indent</button>}
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map(indent => (
                    <div key={indent.indent_id} onClick={() => setSelectedIndentId(indent.indent_id)}
                      className="bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer px-5 py-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-semibold text-gray-900">{indent.indent_code}</span>
                          <StatusBadge s={indent.status}/>
                          {indent.supplier_notified_at && <span className="text-xs text-green-600 flex items-center gap-1"><Send size={11}/>Sent</span>}
                        </div>
                        <p className="text-sm text-gray-600 truncate"><Building2 size={13} className="inline mr-1 text-gray-400"/>{indent.supplier_name}</p>
                      </div>
                      <div className="text-right shrink-0"><p className="text-xs text-gray-400">Required by</p><p className="font-medium text-gray-800">{fmtDate(indent.required_date)}</p></div>
                      <div className="text-right shrink-0"><p className="text-xs text-gray-400">{indent.total_items} items</p><p className="font-bold text-gray-900">{fmt(indent.total_value)}</p></div>
                      <ChevronRight size={18} className="text-gray-300 shrink-0"/>
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        )}

        {/* VENDORS */}
        {tab === 'vendors' && (
          vendors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Truck size={40} className="mb-3 opacity-30"/>
              <p className="font-medium">No vendors yet</p>
              <button onClick={() => setShowAddVendor(true)} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg flex items-center gap-2"><Plus size={15}/>Add First Vendor</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {vendors.map(v => (
                <div key={v.vnd_id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div><p className="font-semibold text-gray-900">{v.company_name}</p><p className="text-xs text-gray-400">{v.vnd_code}</p></div>
                    {v.rating && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">★ {v.rating}</span>}
                  </div>
                  {v.contact_person && <p className="text-sm text-gray-600">{v.contact_person}</p>}
                  {v.mobile && <p className="text-sm text-gray-500 flex items-center gap-1"><Phone size={12}/>{v.mobile}</p>}
                  {v.email && <p className="text-xs text-gray-400 truncate">{v.email}</p>}
                  <div className="flex items-center justify-between pt-1">
                    {v.city && <span className="text-xs text-gray-400">{v.city}</span>}
                    {v.payment_terms && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{v.payment_terms}</span>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* SUPPLIER RATES */}
        {tab === 'rates' && <SupplierRatesTab vendors={vendors}/>}
      </div>

      {/* Modals */}
      {showCreate && projectId && (
        <CreateIndentModal projectId={projectId} vendors={vendors} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadIndents(); }}
          onNeedVendor={() => { setShowCreate(false); setTab('vendors'); setShowAddVendor(true); }}/>
      )}
      {showAddVendor && (
        <AddVendorModal onClose={() => setShowAddVendor(false)} onCreated={v => { setVendors(prev => [...prev, v]); setShowAddVendor(false); }}/>
      )}
      {selectedIndentId && (
        <IndentDetail indentId={selectedIndentId} onClose={() => setSelectedIndentId(null)} onRefresh={loadIndents}/>
      )}
    </div>
  );
};

export default MaterialIndentPage;
